import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, API_BASE, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AudioPlayer, { INTERVALS } from "@/components/AudioPlayer";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, Search, X } from "lucide-react";

const ERAS = ["Territory", "Golden", "New Generation", "Attitude", "Ruthless Aggression", "PG", "Reality", "Modern"];
const DIFFS = ["easy", "medium", "hard", "expert"];

/** Wrestler picker used in the admin editor. Dropdown-only, no free text. */
function WrestlerPicker({ value, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const debRef = useRef(null);

  // Preload the selected wrestler
  useEffect(() => {
    if (value && (!selected || selected.id !== value)) {
      api.get(`/admin/wrestlers/${value}`).then((r) => setSelected(r.data)).catch(() => {});
    }
    if (!value) setSelected(null);
  }, [value, selected]);

  const search = useCallback(async (query) => {
    try {
      const { data } = await api.get("/admin/wrestlers", { params: query ? { q: query } : {} });
      setResults((data || []).slice(0, 12));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => search(q), 150);
    return () => clearTimeout(debRef.current);
  }, [q, search]);

  const clear = () => { onChange(""); setSelected(null); setQ(""); };

  if (selected) {
    return (
      <div className="flex items-center justify-between p-3 bg-slate-950 border-2 border-emerald-800/50 rounded-xl"
           data-testid="admin-wrestler-selected">
        <div className="min-w-0">
          <div className="font-display font-bold truncate">{selected.primary_name}</div>
          <div className="text-[10px] font-mono uppercase text-slate-500">
            {selected.era || "no era"}
            {selected.former_names?.length ? ` · ${selected.former_names.length} former names` : ""}
            {selected.nicknames?.length ? ` · ${selected.nicknames.length} nicknames` : ""}
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={clear} data-testid="admin-wrestler-clear">
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
      <Input value={q} onChange={(e) => setQ(e.target.value)}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search roster to pick the correct answer"
        data-testid="admin-wrestler-search"
        className="pl-9 bg-slate-950 border-slate-700" />
      {open && results.length > 0 && (
        <div className="absolute left-0 right-0 mt-2 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-50 max-h-64 overflow-y-auto divide-y divide-slate-800">
          {results.map((w) => (
            <button key={w.id}
              onMouseDown={() => { onChange(w.id); setSelected(w); setOpen(false); }}
              className="w-full text-left px-3 py-2.5 text-sm text-slate-200 hover:bg-slate-800/80"
              data-testid={`admin-wrestler-option-${w.id}`}>
              <div className="font-semibold">{w.primary_name}</div>
              <div className="text-[10px] font-mono uppercase text-slate-500">{w.era || "no era"}</div>
            </button>
          ))}
        </div>
      )}
      <div className="text-xs text-slate-500 mt-2">
        No match? <Link to="/admin/wrestlers/new" className="text-red-500 hover:text-red-400 font-semibold">Add a wrestler</Link> to the roster first.
      </div>
    </div>
  );
}

export default function AdminPuzzle() {
  const { id, mode: routeMode } = useParams();
  const nav = useNavigate();
  const editing = !!id;
  const [form, setForm] = useState({
    mode: routeMode || "theme",
    wrestler_id: "",
    audio_start_seconds: 0, era: "", difficulty: "medium",
    description: "",
    audio_source: "upload",
    audio_path: "", reveal_image_path: "",
    soundcloud_url: "", soundcloud_track_id: "",
    scheduled_date: "", status: "draft",
    opponent: "", event: "", year: "", bonus_question: "", bonus_answer: "",
  });
  const [busy, setBusy] = useState(false);
  const [scMeta, setScMeta] = useState(null);
  const [scResolving, setScResolving] = useState(false);
  const [scError, setScError] = useState("");
  const [previewInterval, setPreviewInterval] = useState(0);
  const audioRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    if (editing) {
      api.get(`/admin/puzzles/${id}`).then((r) => {
        const d = r.data;
        setForm({
          ...d,
          wrestler_id: d.wrestler_id || "",
          audio_start_seconds: d.audio_start_seconds || 0,
          scheduled_date: d.scheduled_date || "",
          year: d.year || "",
          audio_source: d.audio_source || "upload",
          soundcloud_url: d.soundcloud_url || "",
          soundcloud_track_id: d.soundcloud_track_id || "",
        });
      });
    }
  }, [id, editing]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFile = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.path;
  };

  const onAudio = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const path = await uploadFile(f, "audio");
      upd("audio_path", path);
      toast.success("Audio uploaded");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || "Upload failed"); }
  };
  const onImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const path = await uploadFile(f, "image");
      upd("reveal_image_path", path);
      toast.success("Image uploaded");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || "Upload failed"); }
  };

  const resolveSoundCloud = async () => {
    if (!form.soundcloud_url.trim()) {
      setScError("Paste a public SoundCloud track URL first.");
      return;
    }
    setScResolving(true); setScError(""); setScMeta(null);
    try {
      const { data } = await api.post("/admin/soundcloud/resolve", { url: form.soundcloud_url.trim() });
      setScMeta(data);
      upd("soundcloud_track_id", data.track_id);
      toast.success("SoundCloud track validated");
    } catch (err) {
      setScError(fmtErr(err.response?.data?.detail) || "Validation failed");
      upd("soundcloud_track_id", "");
    } finally {
      setScResolving(false);
    }
  };

  const save = async (status) => {
    if (!form.wrestler_id) { toast.error("Pick the correct wrestler / team / faction"); return; }
    if (status === "published" && !form.audio_path) {
      toast.error("This SoundCloud embed reveals the answer. Upload an authorized audio file to create a spoiler-free puzzle.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        ...form,
        status,
        audio_source: "upload",
        audio_start_seconds: parseFloat(form.audio_start_seconds) || 0,
        year: form.year ? parseInt(form.year) : null,
        scheduled_date: form.scheduled_date || null,
      };
      if (editing) await api.patch(`/admin/puzzles/${id}`, body);
      else await api.post("/admin/puzzles", body);
      toast.success(status === "published" ? "Published" : "Saved as draft");
      nav("/admin");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const canPreview = !!form.audio_path;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
      <div className="text-xs font-mono uppercase tracking-widest text-red-500 mb-1">Booker's Desk</div>
      <h1 className="font-display text-3xl sm:text-5xl font-black uppercase">
        {editing ? "Edit" : "New"} {form.mode === "theme" ? "Theme" : "Promo"}
      </h1>

      <div className="mt-6 space-y-5">
        <FieldRow label="Correct answer (from wrestler roster)">
          <WrestlerPicker value={form.wrestler_id} onChange={(v) => upd("wrestler_id", v)} />
        </FieldRow>

        <div className="grid grid-cols-2 gap-4">
          <FieldRow label="Era (badge shown on reveal)">
            <Select value={form.era || ""} onValueChange={(v) => upd("era", v)}>
              <SelectTrigger data-testid="admin-era" className="bg-slate-950 border-slate-700">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>{ERAS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
          </FieldRow>
          <FieldRow label="Difficulty">
            <Select value={form.difficulty} onValueChange={(v) => upd("difficulty", v)}>
              <SelectTrigger data-testid="admin-difficulty" className="bg-slate-950 border-slate-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>{DIFFS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </FieldRow>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FieldRow label="Audio start (seconds)">
            <Input type="number" step="0.1" min="0" value={form.audio_start_seconds}
              onChange={(e) => upd("audio_start_seconds", e.target.value)}
              data-testid="admin-audio-start" className="bg-slate-950 border-slate-700" />
          </FieldRow>
          <FieldRow label="Scheduled date">
            <Input type="date" value={form.scheduled_date} onChange={(e) => upd("scheduled_date", e.target.value)}
              data-testid="admin-date" className="bg-slate-950 border-slate-700" />
          </FieldRow>
        </div>

        <FieldRow label="Description / historical fact">
          <Textarea value={form.description} onChange={(e) => upd("description", e.target.value)}
            data-testid="admin-description" className="bg-slate-950 border-slate-700 min-h-20" />
        </FieldRow>

        {/* Authorized Audio (mandatory for gameplay) */}
        <div className="p-4 bg-slate-900/60 border border-slate-800 rounded-xl space-y-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-red-500">Authorized Audio File</div>
            <div className="text-sm text-slate-400">
              Spoiler-safe playable audio. Stored with a randomized filename; the frontend never
              sees a URL, title, artist, or any metadata during gameplay.
            </div>
          </div>

          <FieldRow label="Audio file (MP3, WAV, M4A, AAC)">
            <div className="flex items-center gap-3 flex-wrap">
              <input type="file" accept="audio/*" ref={audioRef} onChange={onAudio} className="hidden" />
              <Button type="button" variant="outline" onClick={() => audioRef.current?.click()}
                data-testid="admin-audio-upload" className="border-slate-700">
                {form.audio_path ? "Replace audio" : "Upload audio"}
              </Button>
              {form.audio_path && (
                <audio controls src={`${API_BASE}/admin/media/audio?path=${encodeURIComponent(form.audio_path)}`}
                  className="h-8" data-testid="admin-audio-preview" />
              )}
              {!form.audio_path && (
                <span className="text-xs text-amber-400 font-mono" data-testid="admin-audio-warning">
                  Required before publishing
                </span>
              )}
            </div>
          </FieldRow>

          <div className="grid grid-cols-2 gap-4">
            <FieldRow label="Song title (revealed after guess)">
              <Input value={form.song_title || ""} onChange={(e) => upd("song_title", e.target.value)}
                data-testid="admin-song-title" placeholder="e.g. Glass Shatters"
                className="bg-slate-950 border-slate-700" />
            </FieldRow>
            <FieldRow label="Artist (revealed after guess)">
              <Input value={form.artist || ""} onChange={(e) => upd("artist", e.target.value)}
                data-testid="admin-artist" placeholder="e.g. Disturbed"
                className="bg-slate-950 border-slate-700" />
            </FieldRow>
          </div>
        </div>

        {/* Optional SoundCloud attribution (post-reveal only) */}
        <div className="p-4 bg-slate-900/60 border border-slate-800 rounded-xl space-y-3">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-red-500">
              SoundCloud Attribution (optional)
            </div>
            <div className="text-sm text-slate-400">
              Only displayed AFTER the puzzle ends. Never rendered during gameplay.
            </div>
          </div>

          <FieldRow label="SoundCloud track URL">
            <div className="flex gap-2">
              <Input value={form.soundcloud_url}
                onChange={(e) => { upd("soundcloud_url", e.target.value); upd("soundcloud_track_id", ""); setScMeta(null); setScError(""); }}
                placeholder="https://soundcloud.com/artist/track (optional)"
                data-testid="admin-sc-url" className="bg-slate-950 border-slate-700" />
              <Button type="button" onClick={resolveSoundCloud} disabled={scResolving || !form.soundcloud_url.trim()}
                data-testid="admin-sc-validate"
                className="bg-red-600 hover:bg-red-500 text-white font-bold uppercase">
                {scResolving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Validate"}
              </Button>
            </div>
          </FieldRow>

          {scError && (
            <div className="text-sm text-red-400 flex items-center gap-2" data-testid="admin-sc-error">
              <AlertTriangle className="h-4 w-4" /> {scError}
            </div>
          )}
          {(scMeta || form.soundcloud_track_id) && !scError && (
            <div className="text-sm text-emerald-400 flex items-center gap-2" data-testid="admin-sc-ok">
              <CheckCircle2 className="h-4 w-4" />
              Validated · track #{form.soundcloud_track_id}
              {scMeta?.author_name && <> · uploader "{scMeta.author_name}"</>}
            </div>
          )}
        </div>

        {canPreview && (
          <div className="p-4 bg-slate-900/60 border border-slate-800 rounded-xl space-y-3">
            <div className="text-xs font-mono uppercase tracking-widest text-red-500">Preview Intervals</div>
            <div className="flex flex-wrap gap-2" data-testid="admin-interval-picker">
              {INTERVALS.map((s, i) => (
                <button key={i} onClick={() => setPreviewInterval(i)}
                  className={`text-xs font-mono px-3 py-1.5 rounded-full border ${
                    i === previewInterval
                      ? "bg-red-600 border-red-500 text-white"
                      : "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"
                  }`}
                  data-testid={`admin-preview-${i}`}>
                  {s < 1 ? s.toFixed(1) : s.toFixed(0)}s
                </button>
              ))}
            </div>
            {form.audio_path && (
              <AudioPlayer
                src={`${API_BASE}/admin/media/audio?path=${encodeURIComponent(form.audio_path)}`}
                startAt={parseFloat(form.audio_start_seconds) || 0}
                maxInterval={INTERVALS[previewInterval]}
              />
            )}
          </div>
        )}

        <FieldRow label="Reveal image (optional)">
          <div className="flex items-center gap-3">
            <input type="file" accept="image/*" ref={imgRef} onChange={onImage} className="hidden" />
            <Button type="button" variant="outline" onClick={() => imgRef.current?.click()}
              data-testid="admin-image-upload" className="border-slate-700">
              {form.reveal_image_path ? "Replace image" : "Upload image"}
            </Button>
            {form.reveal_image_path && <span className="text-xs text-emerald-400 font-mono">✓ uploaded</span>}
          </div>
        </FieldRow>

        {form.mode === "promo" && (
          <div className="p-4 bg-slate-900/50 border border-slate-800 rounded-xl space-y-4">
            <div className="text-xs font-mono uppercase tracking-widest text-red-500">Promo Extras</div>
            <div className="grid grid-cols-2 gap-4">
              <FieldRow label="Opponent"><Input value={form.opponent} onChange={(e) => upd("opponent", e.target.value)} data-testid="admin-opponent" className="bg-slate-950 border-slate-700" /></FieldRow>
              <FieldRow label="Event / show"><Input value={form.event} onChange={(e) => upd("event", e.target.value)} data-testid="admin-event" className="bg-slate-950 border-slate-700" /></FieldRow>
              <FieldRow label="Year"><Input type="number" value={form.year} onChange={(e) => upd("year", e.target.value)} data-testid="admin-year" className="bg-slate-950 border-slate-700" /></FieldRow>
            </div>
            <FieldRow label="Bonus question"><Input value={form.bonus_question} onChange={(e) => upd("bonus_question", e.target.value)} data-testid="admin-bonus-q" className="bg-slate-950 border-slate-700" /></FieldRow>
            <FieldRow label="Bonus answer"><Input value={form.bonus_answer} onChange={(e) => upd("bonus_answer", e.target.value)} data-testid="admin-bonus-a" className="bg-slate-950 border-slate-700" /></FieldRow>
          </div>
        )}

        <div className="flex gap-3 pt-4">
          <Button onClick={() => save("draft")} disabled={busy} variant="outline"
            data-testid="admin-save-draft" className="border-slate-700">
            Save Draft
          </Button>
          <Button onClick={() => save("published")} disabled={busy}
            data-testid="admin-publish" className="bg-red-600 hover:bg-red-500 text-white font-bold uppercase">
            Publish
          </Button>
          <Button variant="ghost" onClick={() => nav("/admin")} data-testid="admin-cancel">Cancel</Button>
        </div>
      </div>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-400 font-mono">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

